import { JSX, useEffect, useRef } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  LoadDateTimeInput,
  LoadSelectInput,
} from 'pages/LoadView/LoadInformation/Components';
import { LoadStopsItemTypes, NormalizedLoad } from 'types/Load';

import {
  PLS_APPOINTMENT_TYPE,
  PLS_APPOINTMENT_TYPE_OPTIONS,
  PLS_FIELD_LIMITS,
  PLS_STOP_ACTION_OPTIONS,
} from './constants';
import { isPlsDropoffStopType, isPlsPickupStopType } from './plsRouteStops';
import { PlsFormValues } from './types';

export type PlsRouteStopFormProps = {
  stopIndex: number;
  stopType: LoadStopsItemTypes;
  formMethods: UseFormReturn<PlsFormValues>;
  allowStopActionSelect?: boolean;
};

export function PlsRouteStopForm({
  stopIndex,
  stopType: stopTypeProp,
  formMethods,
  allowStopActionSelect = false,
}: PlsRouteStopFormProps): JSX.Element {
  const { control, getValues, setValue } = formMethods;
  const stopKey = `stops.${stopIndex}` as const;
  const stopTypePath = `${stopKey}.stopType` as FieldPath<NormalizedLoad>;
  const watchedStopType = useWatch({ control, name: stopTypePath });
  const stopType = (watchedStopType || stopTypeProp) as LoadStopsItemTypes;
  const isPickupWindow = isPlsPickupStopType(stopType);

  const apptTypePath = `${stopKey}.apptType` as FieldPath<NormalizedLoad>;
  const latestPath = `${stopKey}.readyEndTime` as FieldPath<NormalizedLoad>;
  const apptType = useWatch({ control, name: apptTypePath });
  const isByAppointment = apptType === PLS_APPOINTMENT_TYPE.ByAppointment;

  const previousStopTypeRef = useRef(stopType);

  useEffect(() => {
    if (!isByAppointment) {
      return;
    }

    setValue(latestPath, '', { shouldDirty: false });
  }, [isByAppointment, latestPath, setValue]);

  // When the user switches Pickup ↔ Dropoff, migrate the earliest time so the
  // newly visible required window field is not left empty.
  useEffect(() => {
    if (!allowStopActionSelect) {
      previousStopTypeRef.current = stopType;
      return;
    }

    const previous = previousStopTypeRef.current;
    previousStopTypeRef.current = stopType;

    if (previous === stopType) {
      return;
    }

    if (isPlsDropoffStopType(stopType) && isPlsPickupStopType(previous)) {
      const readyTime = getValues(
        `${stopKey}.readyTime` as FieldPath<PlsFormValues>
      );
      const mustDeliver = getValues(
        `${stopKey}.mustDeliver` as FieldPath<PlsFormValues>
      );
      if (readyTime && !mustDeliver) {
        setValue(
          `${stopKey}.mustDeliver` as FieldPath<PlsFormValues>,
          readyTime,
          { shouldDirty: true }
        );
      }
      // Dropoffs submit mustDeliver; leftover readyTime can resurrect on payload.
      setValue(`${stopKey}.readyTime` as FieldPath<PlsFormValues>, '', {
        shouldDirty: true,
      });
      return;
    }

    if (isPlsPickupStopType(stopType) && isPlsDropoffStopType(previous)) {
      const mustDeliver = getValues(
        `${stopKey}.mustDeliver` as FieldPath<PlsFormValues>
      );
      const readyTime = getValues(
        `${stopKey}.readyTime` as FieldPath<PlsFormValues>
      );
      if (mustDeliver && !readyTime) {
        setValue(
          `${stopKey}.readyTime` as FieldPath<PlsFormValues>,
          mustDeliver,
          { shouldDirty: true }
        );
      }
      setValue(`${stopKey}.mustDeliver` as FieldPath<PlsFormValues>, '', {
        shouldDirty: true,
      });
    }
  }, [allowStopActionSelect, getValues, setValue, stopKey, stopType]);

  const earliestLabel = isByAppointment
    ? 'Appointment Date/Time'
    : 'Earliest Date/Time';

  return (
    <Flex direction='col' align='stretch' gap='md' className='w-full'>
      {allowStopActionSelect ? (
        <div>
          <Label name={stopTypePath} required className='text-neutral-800'>
            Stop Action
          </Label>
          <Controller
            name={stopTypePath}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={
                  isPlsDropoffStopType(field.value as string)
                    ? LoadStopsItemTypes.Dropoff
                    : LoadStopsItemTypes.Pickup
                }
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent>
                  {PLS_STOP_ACTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      ) : null}

      <RHFTextInput
        name={`${stopKey}.address.name` as FieldPath<PlsFormValues>}
        label='Name'
        placeholder='Location name'
      />

      <RHFTextInput
        name={`${stopKey}.address.addressLine1` as FieldPath<PlsFormValues>}
        label='Address Line 1'
        placeholder='Street address'
        required
      />

      <RHFTextInput
        name={`${stopKey}.address.addressLine2` as FieldPath<PlsFormValues>}
        label='Address Line 2'
        placeholder='Suite, unit, etc.'
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name={`${stopKey}.address.city` as FieldPath<PlsFormValues>}
          label='City'
          required
          placeholder='City'
        />
        <RHFTextInput
          name={`${stopKey}.address.state` as FieldPath<PlsFormValues>}
          label='State'
          required
          placeholder='State'
        />
      </Grid>

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name={`${stopKey}.address.zip` as FieldPath<PlsFormValues>}
          label='Zip Code'
          required
          placeholder='Zip code'
        />
        <RHFTextInput
          name={`${stopKey}.address.country` as FieldPath<PlsFormValues>}
          label='Country'
          placeholder='Country'
        />
      </Grid>

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput
          name={`${stopKey}.contact` as FieldPath<PlsFormValues>}
          label='Contact'
          placeholder='Contact name'
        />
        <RHFTextInput
          name={`${stopKey}.phone` as FieldPath<PlsFormValues>}
          label='Phone'
          placeholder='(555) 123-4567'
        />
      </Grid>

      {/* PLS books the stop's own number here: pickupNumber on a pickup, deliveryNumber on a drop. */}
      <RHFTextInput
        name={`${stopKey}.refNumber` as FieldPath<PlsFormValues>}
        label={isPickupWindow ? 'Pickup #' : 'Delivery #'}
        placeholder={isPickupWindow ? 'Pickup number' : 'Delivery number'}
        maxLength={PLS_FIELD_LIMITS.referenceNumber}
      />

      <Typography
        variant='h6'
        weight='medium'
        className='text-neutral-800 mt-2'
      >
        Appointment
      </Typography>

      <LoadSelectInput
        name={apptTypePath}
        label='Appointment Type'
        options={[...PLS_APPOINTMENT_TYPE_OPTIONS]}
      />

      {isPickupWindow ? (
        <>
          <LoadDateTimeInput
            name={`${stopKey}.readyTime` as FieldPath<NormalizedLoad>}
            label={earliestLabel}
            load={getValues()}
            required
          />
          {!isByAppointment && (
            <LoadDateTimeInput
              name={`${stopKey}.readyEndTime` as FieldPath<NormalizedLoad>}
              label='Latest Date/Time'
              load={getValues()}
            />
          )}
        </>
      ) : (
        <>
          <LoadDateTimeInput
            name={`${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>}
            label={earliestLabel}
            load={getValues()}
            required
          />
          {!isByAppointment && (
            <LoadDateTimeInput
              name={`${stopKey}.readyEndTime` as FieldPath<NormalizedLoad>}
              label='Latest Date/Time'
              load={getValues()}
            />
          )}
        </>
      )}
    </Flex>
  );
}
